import { useMemo } from 'react';

/**
 * PaceIndicator
 *
 * Horizontal gauge that highlights how close the user's current pace is
 * to the brisk-walking sweet-spot of ~5 km/h.
 *
 * Layout (left → right):
 *   red ──fade──▶ green (center @ 5 km/h) ──fade──▶ red
 *   range: 0 → 10 km/h
 *
 * The needle slides smoothly based on `speedKmh`. A small chip below
 * shows the current numeric value + a status label.
 *
 * Props:
 *  - speedKmh: number (current speed in km/h)
 *  - isDark: boolean (theme)
 *  - t: i18n translator (fallback string supported)
 */
export default function PaceIndicator({ speedKmh = 0, isDark = false, t = (_, fb) => fb }) {
  const MIN = 0;
  const MAX = 10;
  const TARGET = 5;
  const TOLERANCE = 0.5; // ±0.5 km/h → considered "in zone"

  const clampedSpeed = Math.max(MIN, Math.min(speedKmh, MAX));
  const positionPct = useMemo(
    () => ((clampedSpeed - MIN) / (MAX - MIN)) * 100,
    [clampedSpeed],
  );

  // Status zones (matching the gradient bar colors):
  //   0–3 km/h    → low       (red left)
  //   3–4.5 km/h  → warming   (orange transition)
  //   4.5–5.5 km/h → ok       (green center)
  //   5.5–7 km/h  → cooling   (orange transition)
  //   7–10 km/h   → high      (red right)
  let status = 'low';
  if (Math.abs(speedKmh - TARGET) <= TOLERANCE) status = 'ok';
  else if (speedKmh > 7) status = 'high';
  else if (speedKmh > TARGET + TOLERANCE) status = 'cooling';
  else if (speedKmh > 3) status = 'warming';

  const statusLabel = {
    low: t('walk.paceLow'),
    warming: t('walk.paceWarming', 'Aumenta'),
    ok: t('walk.paceOk'),
    cooling: t('walk.paceCooling', 'Rallenta'),
    high: t('walk.paceHigh'),
  }[status];

  const statusColor = {
    low: 'text-red-400',
    warming: 'text-amber-400',
    ok: 'text-emerald-400',
    cooling: 'text-amber-400',
    high: 'text-red-400',
  }[status];

  // Color of the moving needle matches the zone
  const needleColor = {
    low: '#EF4444',
    warming: '#F59E0B',
    ok: '#10B981',
    cooling: '#F59E0B',
    high: '#EF4444',
  }[status];

  return (
    <div
      className={`rounded-2xl px-4 py-3 ${
        isDark ? 'bg-white/[0.04] border border-white/10' : 'bg-white border border-gray-200'
      }`}
      data-testid="pace-indicator"
      aria-label={`Pace ${clampedSpeed.toFixed(1)} km/h — ${statusLabel}`}
    >
      <div className="flex items-center justify-center mb-2 relative">
        <span
          className={`absolute left-0 text-[11px] uppercase tracking-wide font-medium ${
            isDark ? 'text-white/55' : 'text-gray-500'
          }`}
        >
          {t('walk.paceTitle')}
        </span>
        {/* Status label centered above the bar — visually aligned to
            the active zone via the dynamic color (red/orange/green). */}
        <span className={`text-xs font-semibold ${statusColor}`} data-testid="pace-indicator-status">
          {statusLabel}
        </span>
      </div>

      {/* Gradient bar */}
      <div className="relative h-3 rounded-full overflow-visible">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'linear-gradient(90deg, #EF4444 0%, #F59E0B 30%, #10B981 50%, #F59E0B 70%, #EF4444 100%)',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.15)',
          }}
        />
        {/* Center marker (5 km/h reference) */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-px h-4"
          style={{ left: '50%', background: 'rgba(255,255,255,0.6)' }}
          aria-hidden="true"
        />

        {/* Needle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
          style={{
            left: `${positionPct}%`,
            // Slower easing so the indicator glides instead of snapping.
            // 1200ms matches the rough cadence of a brisk human stride and
            // makes the UI feel calm even with chatty GPS fixes.
            transition: 'left 1200ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
          data-testid="pace-indicator-needle"
        >
          <div
            className="w-4 h-4 rounded-full border-2 border-white shadow-md"
            style={{ background: needleColor }}
          />
        </div>
      </div>

      {/* Scale + current speed */}
      <div className="flex items-center justify-between mt-2">
        <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>0</span>
        <span
          className={`text-xs font-semibold ${isDark ? 'text-white/85' : 'text-gray-700'}`}
          data-testid="pace-indicator-value"
        >
          {clampedSpeed.toFixed(1)} km/h
        </span>
        <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>10</span>
      </div>
    </div>
  );
}
